import { useState, useEffect } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Calendar,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User,
  Eye,
  Search,
} from "lucide-react";
import {
  impedimentoService,
  datasComImpedimentoService,
  usuarioService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { formatarData, obterDataAtual } from "../../utils/FormatarData";
import PageContainer from "../../components/PageContainer";

const GerenciamentoImpedimentos = () => {
  const [activeTab, setActiveTab] = useState("impedimentos");
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();

  // Estados para pesquisa
  const [pesquisaImpedimentos, setPesquisaImpedimentos] = useState("");
  const [pesquisaDatas, setPesquisaDatas] = useState("");

  // Estados para Impedimentos
  const [impedimentos, setImpedimentos] = useState([]);
  const [showImpedimentoForm, setShowImpedimentoForm] = useState(false);
  const [editingImpedimento, setEditingImpedimento] = useState(null);
  const [impedimentoFormData, setImpedimentoFormData] = useState({
    nome: "",
  });

  // Estados para Datas com Impedimento
  const [datasImpedimento, setDatasImpedimento] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [showDataForm, setShowDataForm] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [dataFormData, setDataFormData] = useState({
    impedimento: { id: "", nome: "" },
    usuario: null,
    datas: [],
    datasFixas: false,
  });

  // Estados para o calendário
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState(new Set());

  // Estados para o modal de visualização de datas
  const [showDatasModal, setShowDatasModal] = useState(false);
  const [selectedItemDatas, setSelectedItemDatas] = useState(null);

  // Estados de paginação
  const [paginacaoImpedimentos, setPaginacaoImpedimentos] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });

  const [paginacaoDatas, setPaginacaoDatas] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });

  useEffect(() => {
    loadImpedimentos();
    loadDatasImpedimento();
    loadUsuarios();
  }, []);

  // Reset páginas quando trocar de aba
  useEffect(() => {
    setPaginacaoImpedimentos((prev) => ({ ...prev, paginaAtual: 1 }));
    setPaginacaoDatas((prev) => ({ ...prev, paginaAtual: 1 }));
  }, [activeTab]);

  // ==================== USUÁRIOS ====================
  const loadUsuarios = async () => {
    try {
      const data = await usuarioService.listarUsuarios();
      // Filtrar apenas usuários ativos
      setUsuarios(data.filter((u) => u.ativo));
    } catch (err) {}
  };

  // ==================== IMPEDIMENTOS ====================
  const loadImpedimentos = async (nome = "") => {
    setLoading(true);
    try {
      const data = await impedimentoService.listaImpedimentos(
        nome || undefined
      );
      setImpedimentos(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handlePesquisaImpedimentos = () => {
    setPaginacaoImpedimentos((prev) => ({ ...prev, paginaAtual: 1 }));
    loadImpedimentos(pesquisaImpedimentos);
  };

  const handleLimparPesquisaImpedimentos = () => {
    setPesquisaImpedimentos("");
    setPaginacaoImpedimentos((prev) => ({ ...prev, paginaAtual: 1 }));
    loadImpedimentos();
  };

  const handleImpedimentoChange = (e) => {
    const { name, value } = e.target;
    setImpedimentoFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImpedimentoSubmit = async () => {
    if (!impedimentoFormData.nome.trim()) {
      showError("Nome é obrigatório");
      return;
    }

    setLoading(true);
    try {
      if (editingImpedimento) {
        await impedimentoService.atualizarImpedimentos(
          editingImpedimento.id,
          impedimentoFormData
        );
        showSuccess("Impedimento atualizado com sucesso!");
      } else {
        await impedimentoService.cadastrarImpedimento(impedimentoFormData);
        showSuccess("Impedimento criado com sucesso!");
      }

      await loadImpedimentos(pesquisaImpedimentos);
      handleImpedimentoCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar impedimento");
    } finally {
      setLoading(false);
    }
  };

  const handleImpedimentoEdit = (item) => {
    setEditingImpedimento(item);
    setImpedimentoFormData({
      nome: item.nome || "",
    });
    setShowImpedimentoForm(true);
  };

  const handleImpedimentoDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir este impedimento?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await impedimentoService.deletarImpedimentos(id);
      showSuccess("Impedimento excluído com sucesso!");
      await loadImpedimentos(pesquisaImpedimentos);
    } catch (err) {
      showError(err.message || "Erro ao excluir impedimento");
    } finally {
      setLoading(false);
    }
  };

  const handleImpedimentoCancel = () => {
    setShowImpedimentoForm(false);
    setEditingImpedimento(null);
    setImpedimentoFormData({ nome: "" });
  };

  const handleImpedimentoNew = () => {
    setEditingImpedimento(null);
    setImpedimentoFormData({ nome: "" });
    setShowImpedimentoForm(true);
  };

  // ==================== DATAS COM IMPEDIMENTO ====================
  const loadDatasImpedimento = async (nome = "") => {
    setLoading(true);
    try {
      const data = await datasComImpedimentoService.listaDatasComImpedimentos(
        nome || undefined
      );
      setDatasImpedimento(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handlePesquisaDatas = () => {
    setPaginacaoDatas((prev) => ({ ...prev, paginaAtual: 1 }));
    loadDatasImpedimento(pesquisaDatas);
  };

  const handleLimparPesquisaDatas = () => {
    setPesquisaDatas("");
    setPaginacaoDatas((prev) => ({ ...prev, paginaAtual: 1 }));
    loadDatasImpedimento();
  };

  const handleImpedimentoSelect = (e) => {
    const impedimentoId = e.target.value;
    const impedimento = impedimentos.find((i) => i.id === impedimentoId) || {
      id: "",
      nome: "",
    };
    setDataFormData((prev) => ({
      ...prev,
      impedimento: impedimento,
    }));
  };

  const handleUsuarioSelect = (e) => {
    const usuarioId = e.target.value;
    const usuario = usuarioId
      ? usuarios.find((u) => u.id === usuarioId) || null
      : null;
    setDataFormData((prev) => ({
      ...prev,
      usuario: usuario,
    }));
  };

  // ==================== MODAL DE VISUALIZAÇÃO DE DATAS ====================
  const handleViewDatas = (item) => {
    setSelectedItemDatas(item);
    setShowDatasModal(true);
  };

  const closeDatasModal = () => {
    setShowDatasModal(false);
    setSelectedItemDatas(null);
  };

  // ==================== CALENDÁRIO ====================
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Dias vazios no início
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Dias do mês
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };
  /*
  const formatDateString = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
*/
  const toggleDateSelection = (date) => {
    if (!date) return;

    const dateString = formatarData(date);
    const newSelectedDates = new Set(selectedDates);

    if (newSelectedDates.has(dateString)) {
      newSelectedDates.delete(dateString);
    } else {
      newSelectedDates.add(dateString);
    }

    setSelectedDates(newSelectedDates);
    setDataFormData((prev) => ({
      ...prev,
      datas: Array.from(newSelectedDates).sort(),
    }));
  };

  const clearAllSelectedDates = async () => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja limpar todas as datas selecionadas?",
    });

    if (!confirmed) {
      return;
    }

    setSelectedDates(new Set());
    setDataFormData((prev) => ({
      ...prev,
      datas: [],
    }));
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const navigateYear = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setFullYear(prev.getFullYear() + direction);
      return newDate;
    });
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    return selectedDates.has(formatarData(date));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleDataSubmit = async () => {
    if (!dataFormData.impedimento.id) {
      showError("Selecione um tipo de impedimento");
      return;
    }

    if (dataFormData.datas.length === 0) {
      showError("Selecione pelo menos uma data no calendário");
      return;
    }

    setLoading(true);
    try {
      if (editingData) {
        // Se está editando e há apenas uma data, fazer update individual
        if (dataFormData.datas.length === 1) {
          const dataParaEnvio = {
            data: formatarData(dataFormData.datas[0]),
            impedimento: dataFormData.impedimento,
            usuario: dataFormData.usuario,
            dataLancamento: obterDataAtual(),
          };

          await datasComImpedimentoService.atualizarDatasComImpedimentos(
            editingData.id,
            dataParaEnvio
          );
          showSuccess("Data com impedimento atualizada com sucesso!");
        } else {
          // Se há múltiplas datas na edição, deletar o registro atual e criar novos
          await datasComImpedimentoService.deletarDatasComImpedimentos(
            editingData.id
          );

          const dataParaEnvio = {
            usuario: dataFormData.usuario,
            impedimento: dataFormData.impedimento,
            datas: dataFormData.datas.map(formatarData),
            datasFixas: dataFormData.datasFixas,
            dataLancamento: obterDataAtual(),
          };

          await datasComImpedimentoService.cadastrarDatasComImpedimentos(
            dataParaEnvio
          );
          showSuccess(
            `Datas com impedimento atualizadas com sucesso! (${dataFormData.datas.length} datas)`
          );
        }
      } else {
        // Formato correto que a API espera
        const dataParaEnvio = {
          usuario: dataFormData.usuario,
          impedimento: dataFormData.impedimento,
          datas: dataFormData.datas.map(formatarData),
          datasFixas: dataFormData.datasFixas,
          dataLancamento: obterDataAtual(),
        };

        await datasComImpedimentoService.cadastrarDatasComImpedimentos(
          dataParaEnvio
        );
        showSuccess(
          `${dataFormData.datas.length} data(s) com impedimento criada(s) com sucesso!`
        );
      }

      await loadDatasImpedimento(pesquisaDatas);
      handleDataCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar datas com impedimento");
    } finally {
      setLoading(false);
    }
  };

  const handleDataEdit = (item) => {
    setEditingData(item);

    // Se o item tem múltiplas datas, usar todas elas
    let datasParaEditar = [];
    if (Array.isArray(item.datas)) {
      datasParaEditar = item.datas.map((data) => formatarData(data));
    } else if (item.data) {
      datasParaEditar = [formatarData(item.data)];
    }

    const dateSet = new Set(datasParaEditar);
    setSelectedDates(dateSet);

    setDataFormData({
      impedimento: item.impedimento || { id: "", nome: "" },
      usuario: item.usuario || null,
      datas: datasParaEditar,
      datasFixas: item.datasFixas,
    });
    setShowDataForm(true);
  };

  const handleDataDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir esta data com impedimento?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await datasComImpedimentoService.deletarDatasComImpedimentos(id);
      showSuccess("Data com impedimento excluída com sucesso!");
      await loadDatasImpedimento(pesquisaDatas);
    } catch (err) {
      showError(err.message || "Erro ao excluir data com impedimento");
    } finally {
      setLoading(false);
    }
  };

  const handleDataCancel = () => {
    setShowDataForm(false);
    setEditingData(null);
    setSelectedDates(new Set());
    setDataFormData({
      impedimento: { id: "", nome: "" },
      usuario: null,
      datas: [],
      datasFixas: false,
    });
    setCurrentDate(new Date());
  };

  const handleDataNew = () => {
    setEditingData(null);
    setSelectedDates(new Set());
    setDataFormData({
      impedimento: { id: "", nome: "" },
      usuario: null,
      datas: [],
      datasFixas: false,
    });
    setCurrentDate(new Date());
    setShowDataForm(true);
  };

  // ==================== FUNÇÕES DE PAGINAÇÃO IMPEDIMENTOS ====================
  const totalRegistrosImpedimentos = impedimentos.length;
  const totalPaginasImpedimentos = Math.ceil(
    totalRegistrosImpedimentos / paginacaoImpedimentos.itensPorPagina
  );
  const indiceInicialImpedimentos =
    (paginacaoImpedimentos.paginaAtual - 1) *
    paginacaoImpedimentos.itensPorPagina;
  const indiceFinalImpedimentos =
    indiceInicialImpedimentos + paginacaoImpedimentos.itensPorPagina;
  const impedimentosPaginados = impedimentos.slice(
    indiceInicialImpedimentos,
    indiceFinalImpedimentos
  );

  const irParaPrimeiraPaginaImpedimentos = () => {
    setPaginacaoImpedimentos((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPaginaImpedimentos = () => {
    setPaginacaoImpedimentos((prev) => ({
      ...prev,
      paginaAtual: totalPaginasImpedimentos,
    }));
  };

  const irParaPaginaAnteriorImpedimentos = () => {
    setPaginacaoImpedimentos((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPaginaImpedimentos = () => {
    setPaginacaoImpedimentos((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginasImpedimentos, prev.paginaAtual + 1),
    }));
  };

  const irParaPaginaImpedimentos = (numeroPagina) => {
    setPaginacaoImpedimentos((prev) => ({
      ...prev,
      paginaAtual: numeroPagina,
    }));
  };

  const gerarNumerosPaginasImpedimentos = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacaoImpedimentos.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(
      totalPaginasImpedimentos,
      inicio + maxPaginasVisiveis - 1
    );

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  // ==================== FUNÇÕES DE PAGINAÇÃO DATAS ====================
  const totalRegistrosDatas = datasImpedimento.length;
  const totalPaginasDatas = Math.ceil(
    totalRegistrosDatas / paginacaoDatas.itensPorPagina
  );
  const indiceInicialDatas =
    (paginacaoDatas.paginaAtual - 1) * paginacaoDatas.itensPorPagina;
  const indiceFinalDatas = indiceInicialDatas + paginacaoDatas.itensPorPagina;
  const datasImpedimentoPaginadas = datasImpedimento.slice(
    indiceInicialDatas,
    indiceFinalDatas
  );

  const irParaPrimeiraPaginaDatas = () => {
    setPaginacaoDatas((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPaginaDatas = () => {
    setPaginacaoDatas((prev) => ({ ...prev, paginaAtual: totalPaginasDatas }));
  };

  const irParaPaginaAnteriorDatas = () => {
    setPaginacaoDatas((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPaginaDatas = () => {
    setPaginacaoDatas((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginasDatas, prev.paginaAtual + 1),
    }));
  };

  const irParaPaginaDatas = (numeroPagina) => {
    setPaginacaoDatas((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const gerarNumerosPaginasDatas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacaoDatas.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginasDatas, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];

  const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Gerenciamento de Impedimentos
        </h1>
        <p className="text-gray-600">
          Gerencie tipos de impedimentos e datas específicas com impedimentos
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("impedimentos")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "impedimentos"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} />
              Tipos de Impedimentos
            </div>
          </button>
          <button
            onClick={() => setActiveTab("datas")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "datas"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              Datas com Impedimento
            </div>
          </button>
        </nav>
      </div>

      {/* Conteúdo das Tabs */}
      {activeTab === "impedimentos" && (
        <div>
          {/* Barra de Pesquisa Impedimentos */}
          <div className="mb-6 flex gap-4 items-center">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Pesquisar por nome..."
                  value={pesquisaImpedimentos}
                  onChange={(e) => setPesquisaImpedimentos(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && handlePesquisaImpedimentos()
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search
                  size={20}
                  className="absolute left-3 top-2.5 text-gray-400"
                />
              </div>
            </div>
            <button
              onClick={handlePesquisaImpedimentos}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Search size={16} />
              Pesquisar
            </button>
            <button
              onClick={handleLimparPesquisaImpedimentos}
              disabled={loading}
              className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <X size={16} />
              Limpar
            </button>
          </div>

          {/* Botão Novo Impedimento */}
          <div className="mb-6">
            <button
              onClick={handleImpedimentoNew}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              Novo Tipo de Impedimento
            </button>
          </div>

          {/* Formulário de Impedimento */}
          {showImpedimentoForm && (
            <div className="mb-6 p-6 bg-white rounded-lg border shadow">
              <h3 className="text-lg font-semibold mb-4">
                {editingImpedimento
                  ? "Editar Tipo de Impedimento"
                  : "Novo Tipo de Impedimento"}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={impedimentoFormData.nome}
                  onChange={handleImpedimentoChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nome do impedimento"
                  maxLength={100}
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleImpedimentoSubmit}
                  disabled={loading || !impedimentoFormData.nome.trim()}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Save size={16} />
                  {loading ? "Salvando..." : "Salvar"}
                </button>

                <button
                  onClick={handleImpedimentoCancel}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <X size={16} />
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Lista de Impedimentos */}
          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Tipos de Impedimentos</h3>
              <span className="text-sm text-gray-500">
                {totalRegistrosImpedimentos} registro(s) encontrado(s)
              </span>
            </div>

            {loading && impedimentos.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Carregando impedimentos...
              </div>
            ) : impedimentos.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Nenhum tipo de impedimento cadastrado
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nome
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {impedimentosPaginados.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <AlertTriangle className="h-5 w-5 text-red-500 mr-3" />
                              <div className="text-sm font-medium text-gray-900">
                                {item.nome}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleImpedimentoEdit(item)}
                                disabled={loading}
                                className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                                title="Editar"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleImpedimentoDelete(item.id)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                                title="Excluir"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Controles de Paginação Impedimentos */}
                {totalPaginasImpedimentos > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        Mostrando {indiceInicialImpedimentos + 1} até{" "}
                        {Math.min(
                          indiceFinalImpedimentos,
                          totalRegistrosImpedimentos
                        )}{" "}
                        de {totalRegistrosImpedimentos} registros
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={irParaPrimeiraPaginaImpedimentos}
                          disabled={paginacaoImpedimentos.paginaAtual === 1}
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Primeira página"
                        >
                          <ChevronsLeft size={16} />
                        </button>

                        <button
                          onClick={irParaPaginaAnteriorImpedimentos}
                          disabled={paginacaoImpedimentos.paginaAtual === 1}
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Página anterior"
                        >
                          <ChevronLeft size={16} />
                        </button>

                        <div className="flex space-x-1">
                          {gerarNumerosPaginasImpedimentos().map(
                            (numeroPagina) => (
                              <button
                                key={numeroPagina}
                                onClick={() =>
                                  irParaPaginaImpedimentos(numeroPagina)
                                }
                                className={`px-3 py-2 rounded-md text-sm font-medium ${
                                  paginacaoImpedimentos.paginaAtual ===
                                  numeroPagina
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                {numeroPagina}
                              </button>
                            )
                          )}
                        </div>

                        <button
                          onClick={irParaProximaPaginaImpedimentos}
                          disabled={
                            paginacaoImpedimentos.paginaAtual ===
                            totalPaginasImpedimentos
                          }
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Próxima página"
                        >
                          <ChevronRight size={16} />
                        </button>

                        <button
                          onClick={irParaUltimaPaginaImpedimentos}
                          disabled={
                            paginacaoImpedimentos.paginaAtual ===
                            totalPaginasImpedimentos
                          }
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Última página"
                        >
                          <ChevronsRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === "datas" && (
        <div>
          {/* Barra de Pesquisa Datas */}
          <div className="mb-6 flex gap-4 items-center">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Pesquisar por nome..."
                  value={pesquisaDatas}
                  onChange={(e) => setPesquisaDatas(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handlePesquisaDatas()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search
                  size={20}
                  className="absolute left-3 top-2.5 text-gray-400"
                />
              </div>
            </div>
            <button
              onClick={handlePesquisaDatas}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Search size={16} />
              Pesquisar
            </button>
            <button
              onClick={handleLimparPesquisaDatas}
              disabled={loading}
              className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <X size={16} />
              Limpar
            </button>
          </div>

          {/* Botão Nova Data */}
          <div className="mb-6">
            <button
              onClick={handleDataNew}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              Nova(s) Data(s) com Impedimento
            </button>
          </div>

          {/* Lista de Datas com Impedimento */}
          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Datas com Impedimento</h3>
              <span className="text-sm text-gray-500">
                {totalRegistrosDatas} registro(s) encontrado(s)
              </span>
            </div>

            {loading && datasImpedimento.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Carregando datas com impedimento...
              </div>
            ) : datasImpedimento.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Nenhuma data com impedimento cadastrada
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Datas
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo de Impedimento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Usuário
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data Lançamento
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {datasImpedimentoPaginadas.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() => handleViewDatas(item)}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors ml-2"
                                title="Ver datas"
                              >
                                <Eye size={16} />
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
                              <div className="text-sm text-gray-900">
                                {item.impedimento?.nome || "-"}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {item.usuario ? (
                              <div className="flex items-center">
                                <User className="h-4 w-4 text-green-500 mr-2" />
                                <div className="text-sm text-gray-900">
                                  {item.usuario.nome}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500 italic">
                                Impedimento geral
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {item.dataLancamento
                                ? formatarData(item.dataLancamento)
                                : "-"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleDataEdit(item)}
                                disabled={loading}
                                className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                                title="Editar"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDataDelete(item.id)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                                title="Excluir"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Controles de Paginação Datas */}
                {totalPaginasDatas > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        Mostrando {indiceInicialDatas + 1} até{" "}
                        {Math.min(indiceFinalDatas, totalRegistrosDatas)} de{" "}
                        {totalRegistrosDatas} registros
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={irParaPrimeiraPaginaDatas}
                          disabled={paginacaoDatas.paginaAtual === 1}
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Primeira página"
                        >
                          <ChevronsLeft size={16} />
                        </button>

                        <button
                          onClick={irParaPaginaAnteriorDatas}
                          disabled={paginacaoDatas.paginaAtual === 1}
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Página anterior"
                        >
                          <ChevronLeft size={16} />
                        </button>

                        <div className="flex space-x-1">
                          {gerarNumerosPaginasDatas().map((numeroPagina) => (
                            <button
                              key={numeroPagina}
                              onClick={() => irParaPaginaDatas(numeroPagina)}
                              className={`px-3 py-2 rounded-md text-sm font-medium ${
                                paginacaoDatas.paginaAtual === numeroPagina
                                  ? "bg-blue-600 text-white"
                                  : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {numeroPagina}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={irParaProximaPaginaDatas}
                          disabled={
                            paginacaoDatas.paginaAtual === totalPaginasDatas
                          }
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Próxima página"
                        >
                          <ChevronRight size={16} />
                        </button>

                        <button
                          onClick={irParaUltimaPaginaDatas}
                          disabled={
                            paginacaoDatas.paginaAtual === totalPaginasDatas
                          }
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Última página"
                        >
                          <ChevronsRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Formulário de Data - CORRIGIDO COM SCROLL */}
      {showDataForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header fixo */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold">
                {editingData
                  ? "Editar Data com Impedimento"
                  : "Nova(s) Data(s) com Impedimento"}
              </h3>
            </div>

            {/* Conteúdo com scroll */}
            <div className="flex-1 overflow-y-auto p-6">
              {editingData && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Modo de Edição:</strong> Você pode adicionar ou
                    remover datas usando o calendário abaixo. Se você selecionar
                    múltiplas datas, o registro atual será substituído pelas
                    novas datas selecionadas.
                  </p>
                </div>
              )}

              {/* Informações básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Impedimento *
                  </label>
                  <select
                    value={dataFormData.impedimento.id}
                    onChange={handleImpedimentoSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione um impedimento</option>
                    {impedimentos.map((impedimento) => (
                      <option key={impedimento.id} value={impedimento.id}>
                        {impedimento.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usuário Específico
                    <span className="text-xs text-gray-500 ml-1">
                      (opcional)
                    </span>
                  </label>
                  <select
                    value={dataFormData.usuario?.id || ""}
                    onChange={handleUsuarioSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Impedimento geral (todos)</option>
                    {usuarios.map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nome}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Deixe em branco para impedimento geral ou selecione para
                    casos específicos como férias
                  </p>
                </div>
              </div>

              {/* Calendário para seleção de datas */}
              <div className="mb-6">
                <h4 className="text-md font-medium mb-4">
                  {editingData ? "Editar Datas" : "Selecionar Datas"}
                </h4>

                <div className="bg-gray-50 border rounded-lg p-4">
                  {/* Navegação do calendário */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigateYear(-1)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Ano anterior"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigateMonth(-1)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Mês anterior"
                      >
                        <ChevronLeft size={16} />
                      </button>
                    </div>

                    <h4 className="text-lg font-semibold">
                      {monthNames[currentDate.getMonth()]}{" "}
                      {currentDate.getFullYear()}
                    </h4>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigateMonth(1)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Próximo mês"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigateYear(1)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Próximo ano"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Calendário */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Cabeçalho dos dias da semana */}
                    {dayNames.map((day) => (
                      <div
                        key={day}
                        className="p-2 text-center text-sm font-medium text-gray-500"
                      >
                        {day}
                      </div>
                    ))}

                    {/* Dias do mês */}
                    {getDaysInMonth(currentDate).map((date, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => toggleDateSelection(date)}
                        disabled={!date}
                        className={`
                            p-2 text-sm rounded transition-colors
                            ${!date ? "invisible" : ""}
                            ${
                              date && isToday(date)
                                ? "ring-2 ring-blue-300"
                                : ""
                            }
                            ${
                              date && isDateSelected(date)
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : "hover:bg-gray-100"
                            }
                          `}
                      >
                        {date ? date.getDate() : ""}
                      </button>
                    ))}
                  </div>

                  {/* Resumo das datas selecionadas */}
                  {selectedDates.size > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Datas selecionadas: {selectedDates.size}
                        </span>
                        <button
                          type="button"
                          onClick={clearAllSelectedDates}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                        >
                          <Trash2 size={14} />
                          Limpar
                        </button>
                      </div>

                      <div className="max-h-24 overflow-y-auto">
                        <div className="flex flex-wrap gap-1">
                          {Array.from(selectedDates)
                            .sort()
                            .map((dateString) => (
                              <span
                                key={dateString}
                                className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded"
                              >
                                {formatarData(dateString)}
                              </span>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/*Campo datasFixas */}
              <div className="mb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="datasFixas"
                    checked={dataFormData.datasFixas}
                    onChange={(e) =>
                      setDataFormData((prev) => ({
                        ...prev,
                        datasFixas: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="datasFixas"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    Datas fixas
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Marque se essas datas são fixas (recorrentes anualmente)
                </p>
              </div>
            </div>

            {/* Footer fixo com botões */}
            <div className="p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
              <div className="flex gap-2">
                <button
                  onClick={handleDataSubmit}
                  disabled={
                    loading ||
                    !dataFormData.impedimento.id ||
                    dataFormData.datas.length === 0
                  }
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Save size={16} />
                  {loading ? "Salvando..." : "Salvar"}
                </button>

                <button
                  onClick={handleDataCancel}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <X size={16} />
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Datas */}
      {showDatasModal && selectedItemDatas && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 flex-shrink-0">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Datas - {selectedItemDatas.impedimento?.nome || "Impedimento"}
                </h3>
                <button
                  onClick={closeDatasModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  {selectedItemDatas.usuario ? (
                    <>
                      <User className="h-4 w-4 text-green-500" />
                      <span>
                        Usuário específico: {selectedItemDatas.usuario.nome}
                      </span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 text-blue-500" />
                      <span>Impedimento geral (todos os usuários)</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6">
              <div className="border-t pt-4">
                <h4 className="text-md font-medium mb-3">
                  Datas (
                  {Array.isArray(selectedItemDatas.datas)
                    ? selectedItemDatas.datas.length
                    : 1}
                  ):
                </h4>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {Array.isArray(selectedItemDatas.datas) ? (
                    selectedItemDatas.datas.map((data, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border"
                      >
                        <Calendar className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium text-blue-800">
                          {formatarData(data)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium text-blue-800">
                        {formatarData(
                          selectedItemDatas.data || selectedItemDatas.datas
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex-shrink-0">
              <div className="flex justify-end">
                <button
                  onClick={closeDatasModal}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default GerenciamentoImpedimentos;
